import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';

import { SaveEmailSubmitButtonGroup, SlideIn } from 'components';
import { HomeFullPageProps } from 'scenes/home/types';
import {
  SegmentedControlQuestion,
  SelectQuestion,
} from 'scenes/common/components/Questions';
import { YesNoAnswer, YES_NO_OPTIONS } from 'scenes/common/types';
import {
  getUnspecifiedPortableContents,
  getUnspecifiedPortableContentsLimit,
  getUnspecifiedPortableLimitRanges,
} from 'scenes/home/selectors';

import UnspecifiedPortableContentsModal from './UnspecifiedPortableContentsModal';

const UNSPECIFIED_PORTABLE_CONTENTS = 'unspecifiedPortableContents';
const UNSPECIFIED_PORTABLE_CONTENTS_LIMIT = 'unspecifiedPortableContentsLimit';

export const FIRST_QUESTION =
  'Would you like cover for unspecified portable contents items Australia-wide?';
export const FIRST_HELP =
  'As part of our portable contents optional extras cover you can choose to select an amount that would cover your unspecified portable contents. Portable contents examples: laptop computers, mobile phones, cameras and hand bags.';

export const SECOND_QUESTION =
  'Please select a limit for your unspecified cover';
export const SECOND_HELP =
  'Apart from any individual items you have specified for Australia-wide cover, you can also take out cover for a group of portable contents items outside the home by choosing one of these limits.';

export const MODAL_LINK_TEXT = 'Which portable contents can be covered?';

const UnspecifiedPortableContentsStep: FunctionComponent<HomeFullPageProps> = ({
  funnelState,
  onUpdateUnspecifiedPortableContents,
  onClickSaveQuoteModal,
  onNext,
}) => {
  const {
    formState: { isValid },
    handleSubmit,
    control,
    errors,
    watch,
  } = useForm({
    mode: 'onChange',
  });

  const [showModal, setShowModal] = useState(false);

  const unspecifiedPortableContents = getUnspecifiedPortableContents(
    funnelState
  );
  const unspecifiedPortableContentsLimit = getUnspecifiedPortableContentsLimit(
    funnelState
  );
  const unspecifiedPortableLimitRanges = getUnspecifiedPortableLimitRanges(
    funnelState
  );

  const watchedOption = watch(
    UNSPECIFIED_PORTABLE_CONTENTS,
    unspecifiedPortableContents
  );

  const handleUnspecifiedContentChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;
      onChange(value);
      if (value === YesNoAnswer.No) {
        onUpdateUnspecifiedPortableContents();
      }
    },
    [onUpdateUnspecifiedPortableContents]
  );

  const handleUnspecifiedContentLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, onChange: Function): void => {
      const { value } = event.target;
      onChange(value);
      if (value && watchedOption === YesNoAnswer.Yes) {
        onUpdateUnspecifiedPortableContents(value);
      }
    },
    [onUpdateUnspecifiedPortableContents, watchedOption]
  );

  return (
    <form onSubmit={handleSubmit(onNext)}>
      <SegmentedControlQuestion
        question={FIRST_QUESTION}
        stateKey={UNSPECIFIED_PORTABLE_CONTENTS}
        options={YES_NO_OPTIONS}
        defaultValue={unspecifiedPortableContents}
        helpText={FIRST_HELP}
        helpLink={{
          id: 'helpLink',
          text: MODAL_LINK_TEXT,
          ariaLabel: 'Which portable contents can be covered? Opens dialog',
          onClick: () => {
            setShowModal(true);
          },
        }}
        changeHandler={handleUnspecifiedContentChange}
        control={control}
        errors={errors}
      />
      {watchedOption === YesNoAnswer.Yes && (
        <SlideIn>
          <SelectQuestion
            question={SECOND_QUESTION}
            stateKey={UNSPECIFIED_PORTABLE_CONTENTS_LIMIT}
            options={unspecifiedPortableLimitRanges}
            defaultValue={unspecifiedPortableContentsLimit}
            helpText={SECOND_HELP}
            isLoading={false}
            control={control}
            changeHandler={handleUnspecifiedContentLimitChange}
          />
        </SlideIn>
      )}
      <UnspecifiedPortableContentsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
      <SaveEmailSubmitButtonGroup
        onClickSaveQuoteModal={onClickSaveQuoteModal}
        submitEnabled={isValid}
      />
    </form>
  );
};

export default UnspecifiedPortableContentsStep;
